import moment from "moment";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Select from "../../../../parts/inputs/select";
import Modal from "../../../../parts/modal-new";
import OptionToggler from "../../../../parts/OptionToggler";
import { canLoadAllOrganizationAthletesForTasks } from "../../../../utils/organization-permissions-helpers";
import { validateSchedule } from "../../../../utils/questionnaire-validations";
import ChooseCoach from "../../../dashboard/settings/manage-coaches/ChooseCoach";
import {
  getDefaultAssignedCoach,
  getUsersAssignableAsCoach,
} from "../../../dashboard/settings/manage-coaches/utils/manage-coaches-utils";

export const AcknowledgeAthleteModal = ({ locals, athlete }) => {
  const { t } = useTranslation("coaching-portal");
  const {
    activeUserToRequest,
    usersOrganization = {
      organizationActiveUsers: [],
      organizationInactiveUsers: [],
    },
    activeUser,
    assert,
    flash,
    organizationRoleName,
  } = locals;
  const referAsClients = Boolean(activeUser.referAsClients);
  const role = referAsClients
    ? t("athletes.roleClientLower")
    : t("athletes.roleAthleteLower");
  const { organizationActiveUsers = [] } = usersOrganization;

  const [selectedOrganizationCoach, setSelectedOrganizationCoach] =
    useState(() =>
      getDefaultAssignedCoach(
        activeUser,
        organizationActiveUsers,
        athlete?.coach?._id ?? athlete?.coachId
      )
    );
  const [selectedGroupNames, setSelectedGroupNames] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [femaleCycleTracking, setFemaleCycleTracking] = useState(false);
  const [frequency, setFrequency] = useState(null);
  const [dayOfWeek, setDayOfWeek] = useState(null);

  const selectOrganizationCoach = (_e, coach) => {
    setSelectedOrganizationCoach(coach);
    setSelectedGroupNames([]);
  };

  const targetCoach = activeUser?.organizationUserStatus
    ? selectedOrganizationCoach
    : activeUser;
  const availableGroups = targetCoach?.athletesGroups || [];
  const availableTemplates =
    activeUserToRequest?.questionnaireTemplates?.templates || [];
  const startingCalendarDay = activeUser.startingCalendarDay || 0;

  const groupCounts = useMemo(() => {
    const map = {};
    (targetCoach?.athletes || []).forEach((a) => {
      (a.groups || []).forEach((g) => {
        map[g] = (map[g] || 0) + 1;
      });
    });
    return map;
  }, [targetCoach]);

  const weekdayOptions = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const name = moment()
          .startOf("week")
          .isoWeekday(i + startingCalendarDay)
          .format("dddd");
        return { text: name, value: name.slice(0, 3).toLowerCase() };
      }),
    [startingCalendarDay]
  );

  const toggleGroup = (name) => {
    setSelectedGroupNames((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const handleFrequencyChange = (value) => {
    setFrequency(value);
    if (!selectedTemplateId && availableTemplates.length) {
      setSelectedTemplateId(availableTemplates[0]._id);
    }
  };

  const computeStartDay = (weekdayValue) => {
    const index = weekdayOptions.findIndex((o) => o.value === weekdayValue);
    if (index < 0) return 0;
    const dayOfTheWeek = moment()
      .startOf("week")
      .add(startingCalendarDay + index, "days");
    const nextStart = moment().isSameOrAfter(dayOfTheWeek)
      ? moment(dayOfTheWeek).add(7, "days")
      : moment(dayOfTheWeek);
    return Number(nextStart.format("YYYYMMDD"));
  };

  const triggerAcknowledgeAthlete = () => {
    let schedule;
    if (frequency && selectedTemplateId) {
      const startDay = computeStartDay(dayOfWeek);
      schedule = { frequency, dayOfWeek, startDay, selectedWeeklyDays: [] };
      if (!validateSchedule(schedule, assert, flash)) return;
    }

    submit(`/coaching-portal/athletes/acknowledge-athlete`, {
      method: "POST",
      body: {
        athleteId: athlete._id,
        coachToId: selectedOrganizationCoach._id,
        coachFromId: athlete?.coach?._id ?? athlete?.coachId,
        groupNames: selectedGroupNames,
        questionnaireTemplateId:
          frequency && selectedTemplateId ? selectedTemplateId : undefined,
        femaleCycleTracking,
        schedule,
      },
    });
  };

  return (
    <Modal
      width={525}
      className="acknowledge-athlete-modal"
      title={t("athletes.acknowledgeAthleteModal.title", {
        role: referAsClients
          ? t("athletes.roleClient")
          : t("athletes.roleAthlete"),
      })}
    >
      <div className="body">
        {activeUser?.organizationUserStatus &&
          canLoadAllOrganizationAthletesForTasks(organizationRoleName) && (
            <>
              <label>
                {t("athletes.acknowledgeAthleteModal.selectCoachLabel", {
                  role,
                })}
              </label>
              <ChooseCoach
                activeUser={activeUser}
                organizationActiveUsers={getUsersAssignableAsCoach(
                  organizationActiveUsers
                )}
                onSelectCoach={selectOrganizationCoach}
                selectedCoach={selectedOrganizationCoach}
                classNames="dropdown-img"
              />
            </>
          )}

        <div className="acknowledge-section">
          <label>{t("athletes.acknowledgeAthleteModal.groupsLabel")}</label>
          {availableGroups.length ? (
            <div className="group-checkbox-list">
              {availableGroups.map((group) => (
                <label key={group.name} className="group-checkbox-list__row">
                  <input
                    type="checkbox"
                    checked={selectedGroupNames.includes(group.name)}
                    onChange={() => toggleGroup(group.name)}
                  />
                  {group.color && (
                    <span
                      className="group-color-dot"
                      style={{ background: group.color }}
                    />
                  )}
                  <span className="group-name">{group.name}</span>
                  <span className="group-count">
                    {groupCounts[group.name] || 0}
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <p className="hint">
              {t("athletes.acknowledgeAthleteModal.groupsEmptyHint", { role })}
            </p>
          )}
        </div>

        <div className="acknowledge-section">
          <label>{t("athletes.acknowledgeAthleteModal.checkInLabel")}</label>
          <OptionToggler
            name="checkInFrequency"
            options={[
              { label: t("questionnaireSchedule.weekly"), value: "weekly" },
              { label: t("questionnaireSchedule.biWeekly"), value: "biweekly" },
              { label: t("questionnaireSchedule.monthly"), value: "monthly" },
            ]}
            defaultValue={null}
            onSelectFn={handleFrequencyChange}
          />
          {frequency && (
            <div className="check-in-fields">
              <div className="check-in-field">
                <Select
                  name="checkInDay"
                  title={t("athletes.acknowledgeAthleteModal.dayLabel")}
                  options={weekdayOptions}
                  value={dayOfWeek}
                  onChange={setDayOfWeek}
                />
              </div>
              {availableTemplates.length ? (
                <div className="check-in-field">
                  <Select
                    name="checkInQuestionnaire"
                    title={t(
                      "athletes.acknowledgeAthleteModal.questionnaireLabel"
                    )}
                    options={availableTemplates.map((tpl) => ({
                      text: tpl.title,
                      value: tpl._id,
                    }))}
                    value={selectedTemplateId}
                    onChange={setSelectedTemplateId}
                  />
                </div>
              ) : (
                <p className="hint">
                  {t("athletes.acknowledgeAthleteModal.checkInEmptyHint")}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="acknowledge-section acknowledge-section--inline">
          <div className="cycle-label-group">
            <p className="cycle-label">
              {t("athletes.acknowledgeAthleteModal.femaleCycleLabel")}
            </p>
            <p className="hint">
              {t("athletes.acknowledgeAthleteModal.femaleCycleHint")}
            </p>
          </div>
          <input
            type="checkbox"
            role="switch"
            name="femaleCycleTracking"
            checked={femaleCycleTracking}
            onChange={(e) => setFemaleCycleTracking(e.target.checked)}
          />
        </div>
      </div>
      <footer className="footer">
        <button
          data-button-type="primary"
          onClick={triggerAcknowledgeAthlete}
          type="button"
        >
          {t("athletes.acknowledgeAthleteModal.acknowledge")}
        </button>
      </footer>
    </Modal>
  );
};
